/**
 * career-llm-settings — the RBAC-gated per-integration LLM configuration surface
 * (route `/settings/ai`, §9.1 / ADR-018 Part 2). For each AI integration (ingest,
 * workshop, generation, matching, verify, brief, org-research, triage) the user may
 * override the model + sampling params; an unset field falls back to the deployed
 * default. The override is SPARSE — only the fields the user sets are stored.
 *
 * SERVER-AUTHORITATIVE gate: the surface renders cards iff the api's `llm.config`
 * returns `canConfigure` (the api reads the VERIFIED token's org + role). The
 * client session gate (`sessionCanConfigureLlm`) only pre-renders a gated hint so an
 * unauthorized caller sees the gated state without a round-trip; the REAL boundary
 * is the server 403 on save. `reasoningEffort` renders ONLY for reasoning-capable
 * models (the allowlist's `supportsReasoning`).
 *
 * `updateSettings` REPLACES the whole overrides object (last-write-wins), so a
 * per-card save posts the WHOLE working map — never just one integration — to avoid
 * wiping the others.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import { I18nToken, SessionToken, apiGet, apiPost } from "../services"
import { sessionCanConfigureLlm, type ShellSession } from "../domain"
import type { CareerI18n, CareerKey } from "../../i18n"

// ─── Wire view types (parsed from the untrusted /api/llm/config record) ─────────

type ReasoningEffort = "low" | "medium" | "high"

interface OverrideView {
  model?: string
  temperature?: number
  topP?: number
  maxTokens?: number
  reasoningEffort?: ReasoningEffort | null
}
interface IntegrationView {
  key: string
  label: string
  description: string
  defaultModel: string
  currentModel: string
  override?: OverrideView
}
interface ModelInfoView {
  id: string
  label: string
  supportsReasoning: boolean
}
interface ConfigView {
  canConfigure: boolean
  integrations: IntegrationView[]
  models: ModelInfoView[]
  /** The opt-in human-review gate (§9.2) — absent/false means off, which is the default. */
  reviewBeforeCall: boolean
}

const num = (v: unknown): number | undefined => (typeof v === "number" ? v : undefined)
const str = (v: unknown): string | undefined => (typeof v === "string" ? v : undefined)

function parseOverride(raw: unknown): OverrideView | undefined {
  if (!raw || typeof raw !== "object") return undefined
  const o = raw as Record<string, unknown>
  const re = o["reasoningEffort"]
  const out: OverrideView = {
    ...(str(o["model"]) ? { model: str(o["model"]) } : {}),
    ...(num(o["temperature"]) !== undefined ? { temperature: num(o["temperature"]) } : {}),
    ...(num(o["topP"]) !== undefined ? { topP: num(o["topP"]) } : {}),
    ...(num(o["maxTokens"]) !== undefined ? { maxTokens: num(o["maxTokens"]) } : {}),
    ...(re === "low" || re === "medium" || re === "high" || re === null ? { reasoningEffort: re } : {}),
  }
  return Object.keys(out).length > 0 ? out : undefined
}

function parseConfig(raw: unknown): ConfigView | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Record<string, unknown>
  const integrations = Array.isArray(r["integrations"])
    ? (r["integrations"] as unknown[]).flatMap((i): IntegrationView[] => {
        const it = (i ?? {}) as Record<string, unknown>
        const key = str(it["key"])
        if (!key) return []
        return [
          {
            key,
            label: str(it["label"]) ?? key,
            description: str(it["description"]) ?? "",
            defaultModel: str(it["defaultModel"]) ?? "",
            currentModel: str(it["currentModel"]) ?? str(it["defaultModel"]) ?? "",
            ...(parseOverride(it["override"]) ? { override: parseOverride(it["override"]) } : {}),
          },
        ]
      })
    : []
  const models = Array.isArray(r["models"])
    ? (r["models"] as unknown[]).flatMap((m): ModelInfoView[] => {
        const mm = (m ?? {}) as Record<string, unknown>
        const id = str(mm["id"])
        if (!id) return []
        return [{ id, label: str(mm["label"]) ?? id, supportsReasoning: mm["supportsReasoning"] === true }]
      })
    : []
  const settings = (r["settings"] ?? {}) as Record<string, unknown>
  return {
    canConfigure: r["canConfigure"] === true,
    integrations,
    models,
    reviewBeforeCall: settings["reviewBeforeCall"] === true,
  }
}

const REASONING_OPTS: readonly ReasoningEffort[] = ["low", "medium", "high"]
const REASONING_KEY: Record<ReasoningEffort, CareerKey> = {
  low: "settings.reasoning.low",
  medium: "settings.reasoning.medium",
  high: "settings.reasoning.high",
}

// ─── ViewModel ──────────────────────────────────────────────────────────────────

type SaveStatus = "saved" | "error"

export class LlmSettingsVm implements ViewModel {
  readonly config: Signal<ConfigView | null> = signal<ConfigView | null>(null)
  readonly loading: Signal<boolean> = signal(true)
  readonly error: Signal<string | null> = signal<string | null>(null)
  /** The whole working overrides map (source of truth for a save — sparse). */
  readonly working: Signal<Record<string, OverrideView>> = signal<Record<string, OverrideView>>({})
  /** Per-integration selected model (drives the reasoning-field visibility). */
  readonly selectedModels: Signal<Record<string, string>> = signal<Record<string, string>>({})
  /** Per-integration transient save feedback. */
  readonly status: Signal<Record<string, SaveStatus>> = signal<Record<string, SaveStatus>>({})
  /** The opt-in review gate as the surface currently shows it (server truth after each save). */
  readonly reviewBeforeCall: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  /** Client-side (session) gate — a pre-render hint only; the server is authoritative. */
  readonly sessionGated: boolean

  constructor(i18n: CareerI18n, session: ShellSession) {
    this.t = i18n.t
    this.sessionGated = !sessionCanConfigureLlm(session)
    this.renderDeps = [this.config, this.loading, this.error, this.working, this.selectedModels, this.status, this.reviewBeforeCall]
  }

  attach(): () => void {
    void this.load()
    return () => {}
  }

  async load(): Promise<void> {
    this.loading.set(true)
    this.error.set(null)
    try {
      const cfg = parseConfig(await apiGet<unknown>("/api/llm/config"))
      this.config.set(cfg)
      // Seed the working map + selected models from the server truth.
      const working: Record<string, OverrideView> = {}
      const models: Record<string, string> = {}
      for (const it of cfg?.integrations ?? []) {
        if (it.override) working[it.key] = it.override
        models[it.key] = it.currentModel || it.defaultModel
      }
      this.working.set(working)
      this.selectedModels.set(models)
      this.reviewBeforeCall.set(cfg?.reviewBeforeCall ?? false)
      this.status.set({})
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loading.set(false)
    }
  }

  modelInfo(id: string): ModelInfoView | undefined {
    return this.config.value?.models.find((m) => m.id === id)
  }

  selectedModel(it: IntegrationView): string {
    return this.selectedModels.value[it.key] ?? it.currentModel ?? it.defaultModel
  }

  supportsReasoning(it: IntegrationView): boolean {
    return this.modelInfo(this.selectedModel(it))?.supportsReasoning ?? false
  }

  /** The saved override for an integration (seeds the param inputs). */
  savedOverride(key: string): OverrideView | undefined {
    return this.working.value[key]
  }

  setSelectedModel(key: string, model: string): void {
    this.selectedModels.set({ ...this.selectedModels.value, [key]: model })
  }

  private flash(key: string, s: SaveStatus): void {
    this.status.set({ ...this.status.value, [key]: s })
    setTimeout(() => {
      const next = { ...this.status.value }
      delete next[key]
      this.status.set(next)
    }, 2200)
  }

  /** Post the WHOLE working map (last-write-wins replaces everything). Reload on success. */
  private async persist(next: Record<string, OverrideView>, key: string): Promise<void> {
    try {
      // Last-write-wins replaces the WHOLE settings object, so the review flag must ride along on
      // every save — posting only `overrides` would silently switch the gate back off.
      await apiPost("/api/llm/settings", {
        overrides: next,
        ...(this.reviewBeforeCall.value ? { reviewBeforeCall: true } : {}),
      })
      this.working.set(next)
      this.flash(key, "saved")
      await this.load()
    } catch {
      this.flash(key, "error")
    }
  }

  /** Save one integration's override from the given draft; empty draft removes it (⇒ default). */
  async save(key: string, draft: OverrideView): Promise<void> {
    const next = { ...this.working.value }
    if (Object.keys(draft).length > 0) next[key] = draft
    else delete next[key]
    await this.persist(next, key)
  }

  /**
   * Flip the opt-in review gate. Saved through the same last-write-wins settings object as the
   * overrides, so the two can never disagree about what is stored.
   */
  async setReviewBeforeCall(on: boolean): Promise<void> {
    const previous = this.reviewBeforeCall.value
    this.reviewBeforeCall.set(on)
    try {
      await apiPost("/api/llm/settings", {
        overrides: this.working.value,
        ...(on ? { reviewBeforeCall: true } : {}),
      })
      await this.load()
    } catch {
      this.reviewBeforeCall.set(previous)
      this.error.set(this.t("settings.saveFailed"))
    }
  }

  /** Reset one integration back to the deployed default (drop its override). */
  async reset(key: string): Promise<void> {
    const next = { ...this.working.value }
    delete next[key]
    await this.persist(next, key)
  }
}

// ─── Element ────────────────────────────────────────────────────────────────────

export class CareerLlmSettings extends KitElement<LlmSettingsVm> {
  protected createViewModel(ctx: KitContext): LlmSettingsVm {
    return new LlmSettingsVm(ctx.inject(I18nToken), ctx.inject(SessionToken))
  }

  private modelSelect(vm: LlmSettingsVm, it: IntegrationView): TemplateResult {
    const selected = vm.selectedModel(it)
    const models = vm.config.value?.models ?? []
    return html`
      <label class="settings-field">
        <span class="settings-field-label" lang="de">${vm.t("settings.model")}</span>
        <select
          class="settings-model"
          data-field="model"
          .value=${selected}
          @change=${(e: Event) => vm.setSelectedModel(it.key, (e.target as HTMLSelectElement).value)}
        >
          ${repeat(
            models,
            (m) => m.id,
            (m) => html`<option value=${m.id} ?selected=${m.id === selected}>
              ${m.label}${m.id === it.defaultModel ? ` · ${vm.t("settings.model.default")}` : ""}
            </option>`,
          )}
        </select>
      </label>
    `
  }

  private numberField(
    vm: LlmSettingsVm,
    labelKey: CareerKey,
    field: string,
    value: number | undefined,
    attrs: { min: number; max: number; step: number },
  ): TemplateResult {
    return html`
      <label class="settings-field settings-field--num">
        <span class="settings-field-label" lang="de">${vm.t(labelKey)}</span>
        <input
          class="settings-num"
          data-field=${field}
          type="number"
          inputmode="decimal"
          min=${attrs.min}
          max=${attrs.max}
          step=${attrs.step}
          placeholder=${vm.t("settings.param.default")}
          .value=${value != null ? String(value) : ""}
        />
      </label>
    `
  }

  private reasoningField(vm: LlmSettingsVm, saved: OverrideView | undefined): TemplateResult {
    const cur = saved?.reasoningEffort ?? ""
    return html`
      <label class="settings-field">
        <span class="settings-field-label" lang="de">${vm.t("settings.reasoning")}</span>
        <select class="settings-reasoning" data-field="reasoning">
          <option value="" ?selected=${cur === "" || cur === null}>${vm.t("settings.reasoning.none")}</option>
          ${REASONING_OPTS.map(
            (r) => html`<option value=${r} ?selected=${cur === r}>${vm.t(REASONING_KEY[r])}</option>`,
          )}
        </select>
      </label>
    `
  }

  private card(vm: LlmSettingsVm, it: IntegrationView): TemplateResult {
    const saved = vm.savedOverride(it.key)
    const overridden = saved !== undefined
    const status = vm.status.value[it.key]
    return html`
      <article
        class=${classMap({ "settings-card": true, "is-active": overridden })}
        data-integration=${it.key}
        aria-labelledby=${`settings-${it.key}-title`}
      >
        <header class="settings-card-head">
          <div class="settings-card-titles">
            <h2 id=${`settings-${it.key}-title`} class="settings-card-title" lang="de">${it.label}</h2>
            <p class="settings-card-desc" lang="de">${it.description}</p>
          </div>
          ${overridden ? html`<span class="settings-badge" lang="de">${vm.t("settings.override")}</span>` : nothing}
        </header>

        <div class="settings-grid">
          ${this.modelSelect(vm, it)}
          ${this.numberField(vm, "settings.temperature", "temperature", saved?.temperature, { min: 0, max: 2, step: 0.1 })}
          ${this.numberField(vm, "settings.topP", "topP", saved?.topP, { min: 0, max: 1, step: 0.05 })}
          ${this.numberField(vm, "settings.maxTokens", "maxTokens", saved?.maxTokens, { min: 1, max: 200000, step: 1 })}
          ${vm.supportsReasoning(it) ? this.reasoningField(vm, saved) : nothing}
        </div>

        <footer class="settings-card-foot">
          <span class="settings-current" lang="de">${vm.t("settings.current", { model: vm.selectedModel(it) })}</span>
          <div class="settings-actions">
            ${overridden
              ? html`<button type="button" class="settings-reset" data-act="reset-llm-settings" data-integration=${it.key} lang="de">
                  ${vm.t("settings.reset")}
                </button>`
              : nothing}
            <button type="button" class="settings-save" data-act="save-llm-settings" data-integration=${it.key} lang="de">
              ${vm.t("settings.save")}
            </button>
            ${status === "saved"
              ? html`<span class="settings-status settings-status--saved" role="status" lang="de">${vm.t("settings.saved")}</span>`
              : status === "error"
                ? html`<span class="settings-status settings-status--error" role="alert" lang="de">${vm.t("settings.saveFailed")}</span>`
                : nothing}
          </div>
        </footer>
      </article>
    `
  }

  /**
   * The opt-in human-review gate (§9.2). A single checkbox row above the integration list —
   * typography and a hairline, not a card (§0). It says plainly what switching it on will do,
   * because switching it on makes every one of YOUR calls stop and wait for you.
   */
  private reviewToggle(vm: LlmSettingsVm): TemplateResult {
    const on = vm.reviewBeforeCall.value
    return html`
      <div class=${classMap({ "settings-review": true, "is-active": on })}>
        <button
          type="button"
          class="settings-review-row"
          data-act="toggle-llm-review"
          data-next=${on ? "off" : "on"}
          role="switch"
          aria-checked=${on ? "true" : "false"}
        >
          <span class="settings-review-switch" aria-hidden="true"></span>
          <span class="settings-review-text">
            <span class="settings-review-label" lang="de">${vm.t("settings.review.label")}</span>
            <span class="settings-review-hint" lang="de">${vm.t("settings.review.hint")}</span>
          </span>
        </button>
      </div>
    `
  }

  protected render(vm: LlmSettingsVm): TemplateResult {
    const cfg = vm.config.value
    const err = vm.error.value

    const body = ((): TemplateResult => {
      if (err) {
        return html`
          <div class="settings-error-box">
            <p class="surface-error" role="alert" lang="de">${vm.t("settings.error", { message: err })}</p>
            <button type="button" data-act="reload-llm-settings" lang="de">${vm.t("settings.retry")}</button>
          </div>
        `
      }
      if (vm.loading.value && cfg === null) {
        return html`<p class="surface-empty" lang="de">${vm.t("settings.loading")}</p>`
      }
      // Server is authoritative: no cards unless the api says the caller may configure.
      if (!cfg || !cfg.canConfigure) {
        return html`<p class="surface-gated" lang="de">${vm.t("gated.hint")}</p>`
      }
      return html`
        <p class="surface-lead" lang="de">${vm.t("settings.hint")}</p>
        ${this.reviewToggle(vm)}
        <div class="settings-list">
          ${repeat(cfg.integrations, (it) => it.key, (it) => this.card(vm, it))}
        </div>
      `
    })()

    // `is-gated` reflects the effective gate (server truth once loaded, else the session hint).
    const gated = cfg ? !cfg.canConfigure : vm.sessionGated
    return html`
      <section class=${classMap({ surface: true, settings: true, "is-gated": gated })} aria-labelledby="career-surface-title">
        <h1 id="career-surface-title" class="surface-title" lang="de">${vm.t("surface.einstellungen.title")}</h1>
        ${body}
      </section>
    `
  }

  /** Read one integration card's draft override from its DOM inputs + the selected model. */
  private readDraft(vm: LlmSettingsVm, card: Element, it: IntegrationView): OverrideView {
    const draft: OverrideView = {}
    const model = vm.selectedModel(it)
    // model: only an override when it differs from the deployed default.
    if (model && model !== it.defaultModel) draft.model = model

    const numOf = (field: string, min: number, max: number, int = false): number | undefined => {
      const raw = card.querySelector<HTMLInputElement>(`input[data-field="${field}"]`)?.value?.trim()
      if (!raw) return undefined
      const n = int ? Number.parseInt(raw, 10) : Number.parseFloat(raw)
      if (!Number.isFinite(n) || n < min || n > max) return undefined
      return n
    }
    const temperature = numOf("temperature", 0, 2)
    const topP = numOf("topP", 0, 1)
    const maxTokens = numOf("maxTokens", 1, 200000, true)
    if (temperature !== undefined) draft.temperature = temperature
    if (topP !== undefined) draft.topP = topP
    if (maxTokens !== undefined) draft.maxTokens = maxTokens

    // reasoning_effort only when the field is shown (reasoning-capable model) and set.
    if (vm.supportsReasoning(it)) {
      const re = card.querySelector<HTMLSelectElement>('select[data-field="reasoning"]')?.value
      if (re === "low" || re === "medium" || re === "high") draft.reasoningEffort = re
    }
    return draft
  }

  protected override actions(vm: LlmSettingsVm): ActionMap {
    const integrationOf = (key: string): IntegrationView | undefined =>
      vm.config.value?.integrations.find((i) => i.key === key)
    return {
      "reload-llm-settings": () => void vm.load(),
      "save-llm-settings": ({ integration }, event) => {
        const it = integration ? integrationOf(integration) : undefined
        const card = (event.target as HTMLElement | null)?.closest(".settings-card")
        if (!it || !card) return
        void vm.save(it.key, this.readDraft(vm, card, it))
      },
      "reset-llm-settings": ({ integration }) => {
        if (integration) void vm.reset(integration)
      },
      // The payload carries the state to move TO, so the handler never re-reads the DOM to
      // decide — a double click cannot land on the value it started from.
      "toggle-llm-review": ({ next }) => void vm.setReviewBeforeCall(next === "on"),
    }
  }
}
